import { createSlice } from '@reduxjs/toolkit';
import { getSynchronisationLogs } from 'api/synchronisation-logs';
import { dispatch } from '../index';
import { store } from 'store';

const initialFilters = {
    success: '',
    hasError: '',
    summaryReportPosted: '',
    dateFrom: '',
    dateTo: ''
};

const createLogTypeState = () => ({
    list: [],
    total: 0,
    currentPage: 1,
    rowsPerPage: 10,
    isLoading: false,
    error: null,
    search: '',
    sortBy: 'logTime',
    sortDirection: 'desc',
    filters: { ...initialFilters }
});

export const logTypeInitialState = createLogTypeState();

const initialState = {
    activeLogType: null,
    deployment: null,
    logTypes: {}
};

const ensureLogTypeState = (state, logTypeId) => {
    if (!logTypeId) return null;

    if (!state.logTypes[logTypeId]) {
        state.logTypes[logTypeId] = createLogTypeState();
    }

    return state.logTypes[logTypeId];
};

const getLogTypeState = (state, logTypeId) => state.logTypes[logTypeId];

const synchronisationLog = createSlice({
    name: 'synchronisationLogs',
    initialState,
    reducers: {
        setDeployment(state, action) {
            state.deployment = action.payload;
        },
        setActiveLogType(state, action) {
            state.activeLogType = action.payload;
            ensureLogTypeState(state, action.payload);
        },
        updateLogType(state, action) {
            const { logTypeId, changes, resetPage = false } = action.payload;
            const logType = getLogTypeState(state, logTypeId);

            if (!logType) return;

            Object.assign(logType, changes);

            if (resetPage) {
                logType.currentPage = 1;
            }
        },
        setFilters(state, action) {
            const { logTypeId, filters } = action.payload;
            const logType = getLogTypeState(state, logTypeId);

            if (!logType) return;

            logType.filters = { ...logType.filters, ...filters };
            logType.currentPage = 1;
        },
        setLogsList(state, action) {
            const { logTypeId, list, total } = action.payload;
            const logType = getLogTypeState(state, logTypeId);

            if (!logType) return;

            logType.list = list;
            logType.total = total;
            logType.error = null;
            logType.isLoading = false;
        },
        setError(state, action) {
            const { logTypeId, error } = action.payload;
            const logType = getLogTypeState(state, logTypeId);

            if (!logType) return;

            logType.error = error;
            logType.isLoading = false;
        },
        resetLogType(state, action) {
            const logTypeId = action.payload;

            if (state.logTypes[logTypeId]) {
                state.logTypes[logTypeId] = createLogTypeState();
            }
        },
        resetIntegrationLogs() {
            return initialState;
        }
    }
});

export default synchronisationLog.reducer;

const { actions } = synchronisationLog;

export function initialLoadLogs(logTypeId, deployment) {
    return async () => {
        dispatch(actions.setDeployment(deployment));
        dispatch(actions.setActiveLogType(logTypeId));
        await getLogsList(logTypeId);
    };
}

export function logsCurrentPage(logTypeId, page) {
    return updateLogsAndFetch(logTypeId, { currentPage: page });
}

export function logsRowsPerPage(logTypeId, rowsPerPage) {
    return updateLogsAndFetch(logTypeId, { rowsPerPage: Number(rowsPerPage) }, true);
}

export function logsSetSort(logTypeId, sortBy, sortDirection) {
    return updateLogsAndFetch(logTypeId, { sortBy, sortDirection }, true);
}

export function searchLogs(logTypeId, search) {
    return updateLogsAndFetch(logTypeId, { search }, true);
}

export function applyLogsFilters(logTypeId, filters) {
    return async () => {
        dispatch(actions.setFilters({ logTypeId, filters }));
        await getLogsList(logTypeId);
    };
}

export function setActiveLogType(logTypeId) {
    return async () => {
        dispatch(actions.setActiveLogType(logTypeId));
    };
}

export function resetIntegrationLogs() {
    return async () => {
        dispatch(actions.resetIntegrationLogs());
    };
}

export function resetLogType(logTypeId) {
    return async () => {
        dispatch(actions.resetLogType(logTypeId));
    };
}

function updateLogsAndFetch(logTypeId, changes, resetPage = false) {
    return async () => {
        dispatch(actions.updateLogType({ logTypeId, changes, resetPage }));
        await getLogsList(logTypeId);
    };
}

async function getLogsList(logTypeId) {
    try {
        dispatch(actions.updateLogType({ logTypeId, changes: { isLoading: true, error: null } }));

        const { synchronisationLog: logStore } = store.getState();
        const logState = logStore.logTypes[logTypeId];

        if (!logState) return;

        const { currentPage, rowsPerPage, search, sortBy, sortDirection, filters } = logState;
        const startIndex = (currentPage - 1) * rowsPerPage;
        const stopIndex = currentPage * rowsPerPage - 1;

        const result = await getSynchronisationLogs({
            deployment: logStore.deployment,
            startIndex,
            stopIndex,
            query: search || '',
            sortBy,
            sortDirection,
            success: filters.success || '',
            hasError: filters.hasError || '',
            summaryReportPosted: filters.summaryReportPosted || '',
            dateFrom: filters.dateFrom || '',
            dateTo: filters.dateTo || ''
        });

        dispatch(
            actions.setLogsList({
                logTypeId,
                list: result.data?.list ?? [],
                total: result.data?.summary?.total ?? 0
            })
        );
    } catch (err) {
        console.log(err);
        dispatch(actions.setError({ logTypeId, error: err.message }));
        throw new Error(err.message);
    }
}
